import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { Link } from "react-router-dom";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
  viewport: { once: true },
};

export default function BlogTestimonialsSection() {
  const [posts, setPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const blogRes = await fetch(`/api/blog`);
        const blogData = await blogRes.json();
        setPosts(blogData || []);
      } catch (err) {
        console.error("Blog load error:", err);
      }
      try {
        const testRes = await fetch(`/api/testimonials`);
        const testData = await testRes.json();
        setTestimonials(testData || []);
      } catch (err) {
        console.error("Testimonials load error:", err);
      }
    };
    loadData();
  }, []);

  return (
    <>
      {/* BLOG */}
      <section id="blog" data-testid="blog-section" className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366] uppercase">BLOG & NEWS</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">
              Ultime <span className="text-[#A3A3A3]">dal blog</span>
            </h2>
            <p className="text-[#A3A3A3] font-body text-sm mt-4">{posts.length} articoli pubblicati</p>
          </motion.div>

          <div className="mt-12 space-y-0">
            {posts.map((post, i) => (
              <motion.div
                key={post.id}
                data-testid={`blog-post-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/blog/${post.id}`}
                  className="blog-card group border-b border-white/10 py-8 flex flex-col sm:flex-row gap-6 items-start cursor-pointer block"
                >
                  <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden border border-white/10">
                    <img
                      src={post.image_url || "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&w=400"}
                      alt={post.title}
                      className="w-full h-full object-cover object-center grayscale-[50%] group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[#FF3366]">{post.category?.toUpperCase()}</span>
                      <span className="font-mono text-[10px] tracking-wider text-[#A3A3A3]">
                        {new Date(post.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl sm:text-2xl font-bold group-hover:text-[#FF3366] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mt-2 font-body leading-relaxed">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-[#FF3366] font-mono text-xs tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                      LEGGI ARTICOLO <ArrowUpRight size={14} />
                    </span>
                  </div>
                  <ArrowUpRight
                    size={24}
                    className="text-white/20 group-hover:text-[#FF3366] transition-colors flex-shrink-0 hidden sm:block"
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-16 sm:py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366] uppercase">TESTIMONIANZE</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">
              Cosa dicono <span className="text-[#A3A3A3]">i nostri clienti</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.id}
                data-testid={`testimonial-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="border border-white/10 bg-[#121212] p-8 hover:border-[#FF3366]/30 transition-colors"
              >
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: t.rating || 5 }).map((_, j) => (
                    <Star key={j} size={16} fill="#FF3366" className="text-[#FF3366]" />
                  ))}
                </div>
                <p className="text-white/90 text-lg font-body leading-relaxed italic">
                  "{t.quote}"
                </p>
                <div className="mt-6 border-t border-white/10 pt-4">
                  <p className="font-heading font-bold text-[#FF3366]">{t.name}</p>
                  <p className="text-[#A3A3A3] font-mono text-xs tracking-wider">
                    {t.role} — {t.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
